/* ==========================================================================
   EOS YMM — Make / Year / Model / Trim fitment
   Data source: metaobject `eos_vehicle` (window.EOSVehicles)
   Product data: metafields eos.fitment / eos.fitment_keys
   ========================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'eos_garage_v1';
  var FILTER_PARAM = 'filter.p.m.eos.fitment_keys';
  var CURRENT_YEAR = new Date().getFullYear() + 1;

  /* ---------------- helpers ---------------- */

  function slug(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function vehicles() {
    return Array.isArray(window.EOSVehicles) ? window.EOSVehicles : [];
  }

  /** "LD (2015–2023)" -> "LD" — the year span is shown separately. */
  function genLabel(v) {
    return String((v && v.g) || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  function yearEnd(v) {
    return v.ye ? v.ye : CURRENT_YEAR;
  }

  function byKey(k) {
    var all = vehicles();
    for (var i = 0; i < all.length; i++) if (all[i].k === k) return all[i];
    return null;
  }

  /** trim_parents arrives as [[child, parent], ...] — turn it into a lookup. */
  function parentMap(v) {
    if (!v) return {};
    if (v._tp) return v._tp;
    var m = {};
    (v.tp || []).forEach(function (pair) {
      if (pair && pair.length === 2) m[pair[0]] = pair[1];
    });
    v._tp = m;
    return m;
  }

  function readGarage() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var g = JSON.parse(raw);
      return g && g.k ? g : null;
    } catch (e) {
      return null;
    }
  }

  function writeGarage(g) {
    try {
      if (g) window.localStorage.setItem(STORE_KEY, JSON.stringify(g));
      else window.localStorage.removeItem(STORE_KEY);
    } catch (e) {
      /* private mode — selection just won't persist */
    }
    document.dispatchEvent(new CustomEvent('eos:garage:change', { detail: g }));
  }

  /** Keys a chosen vehicle should match against a product's fitment_keys. */
  function garageKeys(g) {
    if (!g) return [];
    var keys = [g.k + '|all'];
    if (g.trim) {
      var v = byKey(g.k);
      var parents = parentMap(v);
      var t = g.trim, seen = {}, guard = 0;
      while (t && !seen[t] && guard++ < 8) {
        seen[t] = true;
        keys.push(g.k + '|' + slug(t));
        t = parents[t];
      }
    } else {
      keys.push(g.k);
    }
    return keys;
  }

  function garageLabel(g) {
    if (!g) return '';
    var v = byKey(g.k);
    var gl = genLabel(v);
    var name = v ? v.mk + ' ' + v.md + (gl ? ' ' + gl : '') : g.k;
    return (g.year ? g.year + ' ' : '') + name + (g.trim ? ' ' + g.trim : '');
  }

  /**
   * The values a garage vehicle sends to the collection filter.
   *
   * fitment_keys carry no year, so a product filed under a platform used to match every
   * year that platform spans (a 2025-only GR Corolla splitter showed for a 2024 car).
   * Products that fit only part of their platform's span now carry year-qualified keys
   * ("corvette-c8|z06@2023"); products that fit the whole span keep plain keys. The garage
   * sends both forms and Shopify ORs values inside one filter, so each product matches
   * exactly the years it fits. With no year chosen, every year of the platform is sent so
   * narrow products still show.
   */
  function filterKeys(g) {
    var base = garageKeys(g);
    if (!g) return base;
    var years = [];
    if (g.year) {
      years.push(Number(g.year));
    } else {
      var v = byKey(g.k);
      if (v) for (var y = v.ys; y <= yearEnd(v); y++) years.push(y);
    }
    var out = base.slice();
    years.forEach(function (y) {
      base.forEach(function (k) { out.push(k + '@' + y); });
    });
    return out;
  }

  /* ---------------- URL building ---------------- */

  function isCollectionPage() {
    return /^\/collections\/[^/]+$/.test(window.location.pathname);
  }

  function allCollectionPath() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/collections/all';
  }

  function currentCollectionHandle() {
    var m = window.location.pathname.match(/^\/collections\/([^/]+)/);
    return m ? m[1].toLowerCase() : '';
  }

  /** If this collection is about one particular vehicle, which one? */
  function collectionVehicleKey() {
    var handle = currentCollectionHandle();
    if (!handle) return null;
    var best = null;
    vehicles().forEach(function (v) {
      [v.k, slug(v.md + ' ' + (v.g || '')), slug(v.mk + ' ' + v.md)].forEach(function (token) {
        if (token && token.length > 3 && handle.indexOf(token) !== -1) {
          if (!best || token.length > best.len) best = { k: v.k, len: token.length };
        }
      });
    });
    return best ? best.k : null;
  }

  /**
   * Where a vehicle search should land.
   * Staying inside a vehicle-specific collection (e.g. "Corvette C8") after picking a
   * different car would always return 0 products — send those shoppers to all products.
   */
  function collectionBase(g) {
    if (isCollectionPage()) {
      var owner = collectionVehicleKey();
      if (!owner || (g && owner === g.k)) return window.location.pathname;
    }
    return allCollectionPath();
  }

  function buildFilterUrl(g, base) {
    var target = base || collectionBase(g);
    var url = new URL(target, window.location.origin);
    // keep sorting, drop every other filter — the old ones rarely apply to a new vehicle
    var current = new URLSearchParams(window.location.search);
    if (current.get('sort_by')) url.searchParams.set('sort_by', current.get('sort_by'));
    filterKeys(g).forEach(function (k) {
      url.searchParams.append(FILTER_PARAM, k);
    });
    return url.pathname + url.search;
  }

  /* ---------------- selector widget ---------------- */

  function initSelector(root) {
    if (!root || root.dataset.eosReady === 'true') return;
    root.dataset.eosReady = 'true';

    var elMake = root.querySelector('[data-eos-make]');
    var elYear = root.querySelector('[data-eos-year]');
    var elModel = root.querySelector('[data-eos-model]');
    var elTrim = root.querySelector('[data-eos-trim]');
    var elGo = root.querySelector('[data-eos-go]');
    var elReset = root.querySelector('[data-eos-reset]');
    if (!elMake || !elYear || !elModel || !elGo) return;

    var all = vehicles();

    function fill(sel, options, placeholder) {
      sel.innerHTML = '';
      var opt0 = document.createElement('option');
      opt0.value = '';
      opt0.textContent = placeholder;
      sel.appendChild(opt0);
      options.forEach(function (o) {
        var opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = o.label;
        sel.appendChild(opt);
      });
      sel.disabled = options.length === 0;
    }

    function inYear(v, y) {
      return !y || (v.ys <= y && yearEnd(v) >= y);
    }

    function makes() {
      var set = {};
      all.forEach(function (v) { set[v.mk] = true; });
      return Object.keys(set).sort().map(function (m) { return { value: m, label: m }; });
    }

    function years(mk) {
      var set = {};
      all.forEach(function (v) {
        if (v.mk !== mk) return;
        for (var y = v.ys; y <= yearEnd(v); y++) set[y] = true;
      });
      return Object.keys(set)
        .map(Number)
        .sort(function (a, b) { return b - a; })
        .map(function (y) { return { value: String(y), label: String(y) }; });
    }

    function models(mk, y) {
      var list = all
        .filter(function (v) { return v.mk === mk && inYear(v, y); })
        .sort(function (a, b) { return a.md.localeCompare(b.md) || a.ys - b.ys; })
        .map(function (v) {
          var gl = genLabel(v);
          return { value: v.k, label: v.md + (gl ? ' ' + gl : ''), v: v };
        });
      // model names are shown without years; add the span back only where two
      // generations would otherwise read identically (e.g. two S197 Mustangs)
      var seen = {};
      list.forEach(function (o) { seen[o.label] = (seen[o.label] || 0) + 1; });
      return list.map(function (o) {
        if (seen[o.label] > 1) {
          o.label += ' (' + o.v.ys + '–' + (o.v.ye ? o.v.ye : 'Present') + ')';
        }
        return { value: o.value, label: o.label };
      });
    }

    function trims(k, y) {
      var v = byKey(k);
      if (!v || !v.t || !v.t.length) return [];
      return v.t
        .filter(function (t) {
          if (!y || !v.ty || !v.ty[t]) return true;
          return v.ty[t][0] <= y && (v.ty[t][1] || CURRENT_YEAR) >= y;
        })
        .map(function (t) { return { value: t, label: t }; });
    }

    function refreshYear() {
      fill(elYear, elMake.value ? years(elMake.value) : [], 'Select Year');
      refreshModel();
    }
    function refreshModel() {
      fill(elModel, elMake.value ? models(elMake.value, Number(elYear.value) || 0) : [], 'Select Model');
      refreshTrim();
    }
    function refreshTrim() {
      if (!elTrim) return;
      var t = elModel.value ? trims(elModel.value, Number(elYear.value) || 0) : [];
      fill(elTrim, t, t.length ? 'Trim (optional)' : 'Trim');
      refreshGo();
    }
    function refreshGo() {
      elGo.disabled = !elModel.value;
    }

    elMake.addEventListener('change', refreshYear);
    elYear.addEventListener('change', refreshModel);
    elModel.addEventListener('change', refreshTrim);
    if (elTrim) elTrim.addEventListener('change', refreshGo);

    fill(elMake, makes(), 'Select Make');
    refreshYear();

    // pre-select from saved garage
    var g = readGarage();
    if (g) {
      var v = byKey(g.k);
      if (v) {
        elMake.value = v.mk; refreshYear();
        if (g.year) { elYear.value = String(g.year); refreshModel(); }
        elModel.value = v.k; refreshTrim();
        if (elTrim && g.trim) elTrim.value = g.trim;
        refreshGo();
      }
    }
    if (elReset) elReset.hidden = !g;

    elGo.addEventListener('click', function (e) {
      e.preventDefault();
      if (!elModel.value) return;
      var saved = {
        k: elModel.value,
        year: Number(elYear.value) || null,
        trim: (elTrim && elTrim.value) || ''
      };
      writeGarage(saved);
      if ((root.dataset.eosMode || 'browse') === 'stay') {
        closeModal();
        return;
      }
      window.location.href = buildFilterUrl(saved, root.dataset.eosTarget || '');
    });

    if (elReset) {
      elReset.addEventListener('click', function (e) {
        e.preventDefault();
        writeGarage(null);
        elMake.value = '';
        refreshYear();
        elReset.hidden = true;
        if (window.location.search.indexOf(FILTER_PARAM) !== -1) {
          var u = new URL(window.location.href);
          u.searchParams.delete(FILTER_PARAM);
          window.location.href = u.pathname + u.search;
        }
      });
    }
  }

  /* ---------------- modal ---------------- */

  function modal() {
    return document.getElementById('EOSVehicleModal');
  }
  function openModal() {
    var m = modal();
    if (!m) return;
    m.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var sel = m.querySelector('.eos-ymm');
    if (sel) initSelector(sel);
  }
  function closeModal() {
    var m = modal();
    if (!m) return;
    m.hidden = true;
    document.documentElement.style.overflow = '';
  }

  /* ---------------- garage bar ---------------- */

  function renderGarage() {
    var g = readGarage();
    Array.prototype.forEach.call(document.querySelectorAll('[data-eos-garage]'), function (bar) {
      bar.hidden = !g;              // bar only appears once a vehicle is chosen
      var name = bar.querySelector('[data-eos-garage-name]');
      if (g && name) name.textContent = garageLabel(g);
    });
  }

  /* ---------------- product fitment ---------------- */

  /**
   * Parse data-eos-rows: "platform|ys|ye|trim~trim;platform|ys|ye|trim".
   * Returns null when the attribute is absent or unusable, so the caller can
   * fall back to the old key-only check instead of failing closed.
   */
  function parseFitRows(s) {
    var out = [];
    String(s || '').split(';').forEach(function (chunk) {
      var p = chunk.split('|');
      if (p.length < 3 || !p[0]) return;
      out.push({
        k: p[0],
        ys: Number(p[1]) || 0,
        ye: Number(p[2]) || 0,
        t: String(p[3] || '').split('~').filter(Boolean)
      });
    });
    return out.length ? out : null;
  }

  /**
   * Does any fitment row actually cover this garage vehicle?
   *
   * fitment_keys carry the platform but no year, so a key match alone says only
   * "same generation". The rows carry each application's own year span, which is
   * what the customer is buying against.
   *
   * The trim test stays deliberately lenient: a row trim the vehicle catalogue
   * does not know (a spelling we have not mapped yet) is ignored rather than
   * treated as an exclusion, so an unmapped trim can never produce a false
   * "Does not fit".
   */
  function rowsFit(rows, g) {
    var v = byKey(g.k);
    var vocab = ((v && v.t) || []).map(slug);
    var mine = garageKeys(g).map(function (x) { return x.split('|')[1]; }).filter(Boolean);

    return rows.some(function (r) {
      if (r.k !== g.k) return false;

      var ye = r.ye || CURRENT_YEAR;
      if (g.year && !(r.ys <= g.year && ye >= g.year)) return false;

      if (!g.trim || !r.t.length || r.t.indexOf('all') !== -1) return true;
      if (r.t.some(function (x) { return mine.indexOf(x) !== -1; })) return true;
      // none of this row's trims is a trim we know for the vehicle — don't judge
      return !r.t.some(function (x) { return vocab.indexOf(x) !== -1; });
    });
  }

  function renderFitment() {
    var g = readGarage();
    var gkeys = garageKeys(g);

    Array.prototype.forEach.call(document.querySelectorAll('[data-eos-fit-badge]'), function (el) {
      var universal = el.dataset.eosUniversal === 'true';
      var keys = (el.dataset.eosKeys || '').split(',').filter(Boolean);
      var status = el.dataset.eosStatus || '';
      var cls, icon, title, sub;

      var check = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
      var cross = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
      var info = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>';

      if (universal) {
        cls = 'universal'; icon = info;
        title = 'Universal fit';
        sub = 'Not vehicle specific — please check the product details before ordering.';
      } else if (!g) {
        cls = 'unknown'; icon = info;
        title = 'Select your vehicle to check fitment';
        sub = '<button type="button" class="eos-fit__pick" data-eos-open>Choose make, year &amp; model</button>';
      } else {
        var rows = parseFitRows(el.dataset.eosRows);
        var hit = rows
          ? rowsFit(rows, g)
          : filterKeys(g).some(function (k) { return keys.indexOf(k) !== -1; });
        if (hit) {
          cls = 'yes'; icon = check;
          title = 'Fits your ' + garageLabel(g);
          sub = status === 'needs_review'
            ? 'Fitment data is being verified — contact us if you want it confirmed.'
            : 'Confirmed against this product’s fitment table.';
        } else {
          cls = 'no'; icon = cross;
          title = 'Does not fit your ' + garageLabel(g);
          sub = '<button type="button" class="eos-fit__pick" data-eos-open>Change vehicle</button>';
        }
      }

      el.className = 'eos-fit__badge eos-fit__badge--' + cls;
      el.innerHTML = icon + '<span><b>' + title + '</b><small>' + sub + '</small></span>';
    });

    // highlight matching rows in the fitment table
    Array.prototype.forEach.call(document.querySelectorAll('.eos-fittable tbody tr'), function (tr) {
      var k = tr.dataset.eosRowKey || '';
      var t = tr.dataset.eosRowTrim || '';
      var match = false;
      if (g && k === g.k) {
        var y = g.year;
        var ys = Number(tr.dataset.eosRowYs || 0);
        var ye = Number(tr.dataset.eosRowYe || 0) || CURRENT_YEAR;
        var yearOk = !y || (ys <= y && ye >= y);
        var mine = garageKeys(g).map(function (x) { return x.split('|')[1]; });
        var trimOk = !g.trim || !t || t === 'all' || t.split('|').some(function (x) { return mine.indexOf(x) !== -1; });
        match = yearOk && trimOk;
      }
      tr.classList.toggle('is-match', match);
    });
  }

  /* ---------------- fitment table (search + show more) ---------------- */

  function initTables() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-eos-fittable]'), function (wrap) {
      if (wrap.dataset.eosReady === 'true') return;
      wrap.dataset.eosReady = 'true';

      var rows = Array.prototype.slice.call(wrap.querySelectorAll('tbody tr'));
      var search = wrap.querySelector('[data-eos-table-search]');
      var more = wrap.querySelector('[data-eos-table-more]');
      var limit = Number(wrap.dataset.eosLimit || 12);
      var expanded = false;

      function apply() {
        var q = ((search && search.value) || '').trim().toLowerCase();
        var shown = 0;
        rows.forEach(function (tr) {
          var hit = !q || tr.textContent.toLowerCase().indexOf(q) !== -1;
          var visible = hit && (expanded || !!q || shown < limit);
          tr.hidden = !visible;
          if (hit) shown++;
        });
        if (more) {
          more.textContent = expanded ? 'Show fewer' : 'Show all ' + rows.length + ' vehicles';
          more.hidden = !!q || rows.length <= limit;
        }
      }

      if (more) more.addEventListener('click', function () { expanded = !expanded; apply(); });
      if (search) search.addEventListener('input', apply);
      apply();
    });
  }

  /* ----------- hide the raw fitment_keys filter from the theme sidebar ----------- */

  var FILTER_SEL = 'input[name="' + FILTER_PARAM + '"]';
  var FRESH_SEL = FILTER_SEL + ':not([data-eos-seen])';
  var KEY_RX = /^[a-z0-9][a-z0-9-]*(\|[a-z0-9-]+)?(@\d{4})?$/;

  function filterGroupRoot(inp) {
    var el = inp.parentElement, best = null;
    while (el && el !== document.body) {
      if (el.querySelector('input[name^="filter."]:not([name="' + FILTER_PARAM + '"])')) break;
      if (el.querySelector(FILTER_SEL)) best = el;
      el = el.parentElement;
    }
    return best;
  }

  function hideChips() {
    // active-filter chips render the raw key ("corvette-c8|stingray", "corvette-c8|z06@2023")
    var active = {};
    try {
      new URLSearchParams(window.location.search).getAll(FILTER_PARAM).forEach(function (v) { active[v] = true; });
    } catch (e) { /* old browser — fall back to the pattern alone */ }
    Array.prototype.forEach.call(document.querySelectorAll('a'), function (el) {
      if (el.children.length > 1) return;
      var t = (el.textContent || '').trim();
      var looksLikeKey = KEY_RX.test(t) && (t.indexOf('|') !== -1 || t.indexOf('@') !== -1);
      if (t && t.length < 80 && (active[t] || looksLikeKey)) {
        var wrap = el.closest('li') || el;
        wrap.setAttribute('data-eos-hidden', 'true');
      }
    });
  }

  function hideFitmentFilter() {
    var fresh = document.querySelectorAll(FRESH_SEL);
    if (!fresh.length) return;
    Array.prototype.forEach.call(fresh, function (inp) {
      inp.setAttribute('data-eos-seen', '1');
      var g = filterGroupRoot(inp);
      if (g) g.setAttribute('data-eos-hidden', 'true');
    });
    hideChips();
  }

  var hideTimer = null;
  function scheduleHide() {
    if (!document.querySelector(FRESH_SEL)) return;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideFitmentFilter, 50);
  }

  /**
   * /collections/all has the title "ALL" — meaningless once a vehicle filter is on.
   * Hide it (and its breadcrumb crumb) while the fitment filter is active.
   */
  function tidyFilteredHeader() {
    if (window.location.search.indexOf(FILTER_PARAM) === -1) return;
    if (!/^\/collections\/all\/?$/.test(window.location.pathname)) return;
    var h = document.querySelector('.collection-header h1, .section-header h1, .collection-content h1, main h1');
    if (h) h.setAttribute('data-eos-hidden', 'true');
  }

  /* ---------------- boot ---------------- */

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.eos-ymm'), function (el) {
      if (!el.closest('.eos-modal')) initSelector(el);
    });
    initTables();
    renderGarage();
    renderFitment();
    hideFitmentFilter();
    tidyFilteredHeader();
  }

  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-eos-open]');
    if (open) { e.preventDefault(); openModal(); return; }
    var close = e.target.closest('[data-eos-close]');
    if (close) { e.preventDefault(); closeModal(); return; }
    var clear = e.target.closest('[data-eos-clear]');
    if (clear) {
      e.preventDefault();
      writeGarage(null);
      if (window.location.search.indexOf(FILTER_PARAM) !== -1) {
        var u = new URL(window.location.href);
        u.searchParams.delete(FILTER_PARAM);
        window.location.href = u.pathname + u.search;
      }
      return;
    }
    var m = modal();
    if (m && !m.hidden && e.target === m) closeModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  document.addEventListener('eos:garage:change', function () {
    renderGarage();
    renderFitment();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

  // the collection filters re-render over AJAX — keep the fitment group hidden
  if (window.MutationObserver) {
    new MutationObserver(scheduleHide).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.EOSGarage = {
    get: readGarage,
    set: writeGarage,
    keys: garageKeys,
    filterKeys: filterKeys,
    label: garageLabel,
    url: buildFilterUrl,
    open: openModal
  };
})();
